import { useState } from "react";
import { refusalOf } from "./answer";
import {
  moved,
  useCreateActivity,
  useUpdateActivity,
  type ControlActivity,
  type Draft,
} from "./control";
import type { ActivityType } from "./types";
import { CHOICE_TYPES, WATCHED_TYPES } from "./types";
import {
  draftOf,
  guessVerdict,
  revealOf,
  withReveal,
  REVEALS_BY_TYPE,
  type RevealAnimation,
  type RevealOptions,
} from "./reveal";
import { Button } from "../ui/Button";

/**
 * ═══ PRÉPARER UNE ACTIVITÉ ═══
 *
 * ⚠️ CET ÉCRAN MANQUAIT, et son absence rendait toute la régie inutilisable : les
 * routes de création existaient, étaient testées, et rien ne les appelait. On
 * pouvait piloter des activités, pas en créer.
 *
 * ⚠️ LE TYPE SE CHOISIT UNE FOIS ET NE SE CHANGE PLUS. Les réponses déjà
 * enregistrées supposent la forme de l'activité : un quiz devenu nuage de mots
 * laisserait des votes rattachés à des choix qui n'ont plus de sens. Le serveur
 * refuse aussi de le changer ; c'est dit ici pour que la décision se prenne au bon
 * moment.
 *
 * Displayed strings are French: this is a French product.
 */

const TYPES: { value: ActivityType; label: string; help: string }[] = [
  {
    value: "poll",
    label: "Sondage",
    help: "Le public choisit. Pas de bonne réponse, pas de points.",
  },
  {
    value: "quiz",
    label: "Quiz",
    help: "Une bonne réponse, des points, un classement.",
  },
  {
    value: "ballot",
    label: "Vote",
    help: "Assemblée générale : chaque voix pèse son coefficient, et il faut le droit de voter. Ne se vote pas par SMS.",
  },
  {
    value: "questions",
    label: "Questions du public",
    help: "Le public écrit, la régie décide de ce qui s'affiche.",
  },
  {
    value: "cloud",
    label: "Nuage de mots",
    help: "Un mot par personne, qui se dessine à mesure.",
  },
  {
    value: "leaderboard",
    label: "Classement",
    help: "Le podium des points de quiz. Rien à répondre : il se regarde.",
  },
];

type Choice = {
  /** Absent tant que le choix n'existe pas côté serveur. Voir `useUpdateActivity`. */
  id?: number;
  /**
   * ⚠️ UNE CLÉ PROPRE AU FORMULAIRE, parce que les réponses se déplacent maintenant
   * et qu'une clé de POSITION ferait réutiliser les champs par leur rang : on monte
   * une réponse, le curseur reste sur celle d'à côté et la frappe en cours atterrit
   * dans la mauvaise. L'identifiant du serveur ne peut pas servir, une réponse
   * ajoutée n'en a pas encore.
   */
  key: number;
  label: string;
  points: number;
  isRightAnswer: boolean;
};

/* Le compteur des clés du formulaire. Il ne sort jamais d'ici : il ne numérote rien
   d'autre que des lignes à l'écran, le temps qu'elles y soient. */
let nextKey = 0;
const emptyChoice = (): Choice => ({
  key: (nextKey += 1),
  label: "",
  points: 0,
  isRightAnswer: false,
});

export function ActivityEditor({
  onCreated,
  editing,
}: {
  onCreated?: () => void;
  /**
   * ═══ LA MÊME BOÎTE POUR CRÉER ET POUR MODIFIER ═══
   *
   * ⚠️ UN SEUL FORMULAIRE, PAS DEUX. Une seconde version « pour la modification »
   * aurait divergé au premier champ ajouté, et la divergence se serait vue le jour
   * où l'on corrige une activité en séance. Ce qui change tient en trois points :
   * le type ne se choisit plus, les choix gardent leur identifiant, et l'envoi
   * passe par `PUT`.
   */
  editing?: ControlActivity;
}) {
  const isEdit = !!editing;
  const create = useCreateActivity();
  const update = useUpdateActivity(editing?.id ?? 0);
  const save = isEdit ? update : create;

  const [type, setType] = useState<ActivityType>(editing?.type ?? "poll");
  const [title, setTitle] = useState(editing?.title ?? "");
  const [content, setContent] = useState(editing?.content ?? "");
  const [choices, setChoices] = useState<Choice[]>(
    editing?.choices?.length
      ? editing.choices.map((c) => ({
          key: (nextKey += 1),
          /* ⚠️ L'IDENTIFIANT VOYAGE AVEC LE CHOIX. C'est lui qui dit au serveur
             « corrige celui-ci » plutôt que « remplace tout » : sans lui, les votes
             déjà exprimés partiraient à la première faute de frappe corrigée. */
          id: c.id,
          label: c.label,
          points: c.points ?? 0,
          isRightAnswer: !!c.isRightAnswer,
        }))
      : [emptyChoice(), emptyChoice()],
  );
  /**
   * ═══ COMBIEN DE RÉPONSES CHACUN PEUT DONNER ═══
   *
   * Le serveur le tient depuis le début (`maxAnswers`, vérifié à l'écriture) et
   * l'écran du participant sait déjà cocher plusieurs cases quand il vaut plus de
   * un. Il n'y avait simplement AUCUN moyen de le régler : toute activité créée ici
   * partait avec le défaut du serveur, une seule réponse. Une possibilité entière,
   * écrite et gardée des deux côtés, injoignable.
   */
  const [perPerson, setPerPerson] = useState(editing?.config?.maxAnswers ?? 1);
  /**
   * ═══ CE QUI SE PASSE QUAND ON MONTRE LES RÉSULTATS ═══
   *
   * Une animation et ses options, gardées même quand on repasse « sans animation » le
   * temps d'hésiter : revenir au podium retrouve ce qu'on y avait réglé. Seul l'envoi
   * décide de ce qui part (voir `withReveal`).
   */
  const [initial] = useState(() => draftOf(editing?.config, editing?.type ?? "poll"));
  const [animation, setAnimation] = useState<RevealAnimation | null>(initial.animation);
  const [options, setOptions] = useState<RevealOptions>(initial.options);
  const tune = (patch: Partial<RevealOptions>) =>
    setOptions((current) => ({ ...current, ...patch }));
  /**
   * ⚠️ LE « POUR » ET L'ABSTENTION DU VERDICT SONT TENUS PAR LEUR CLÉ DE LIGNE, PAS PAR
   * LEUR RANG. Le serveur n'a que le rang (les choix neufs n'ont pas d'identifiant) ;
   * mais tenu par rang dans le formulaire, monter une réponse ou en supprimer une
   * faisait basculer le « pour » sur sa voisine sans que rien ne bouge à l'écran. Le
   * rang se calcule à l'envoi.
   *
   * `undefined` : rien de choisi, le formulaire propose (`guessVerdict`). `null` pour
   * l'abstention : « aucune », choisi exprès.
   */
  const storedVerdict = initial.animation === "verdict";
  const [approveKey, setApproveKey] = useState<number | undefined>(() =>
    storedVerdict ? choices[initial.options.approve]?.key : undefined,
  );
  const [abstainKey, setAbstainKey] = useState<number | null | undefined>(() =>
    storedVerdict
      ? initial.options.abstain === null
        ? null
        : choices[initial.options.abstain]?.key
      : undefined,
  );

  const withChoices = CHOICE_TYPES.includes(type);
  /* Un classement ne pose pas de question : il porte un titre, et c'est tout ce que
     la salle en lira. Demander « Question posée » pour « Le classement » se lit comme
     un formulaire qui n'a pas été relu. */
  const watchedOnly = WATCHED_TYPES.includes(type);
  const filled = choices.filter((c) => c.label.trim());
  /* On ne peut pas en autoriser plus qu'il n'y a de réponses à choisir : le serveur
     refuserait, et un maximum de cinq sur trois cases ne veut rien dire. */
  const ceiling = Math.max(1, filled.length);
  const maxAnswers = Math.min(Math.max(1, perPerson), ceiling);
  const ready = title.trim() && (!withChoices || filled.length > 0);

  /* Une animation qui ne va pas avec le type — choisie avant de changer de type, à la
     création — ne part pas : les résultats resteraient en barres sans qu'on le sache. */
  const allowed = REVEALS_BY_TYPE[type] ?? [];
  const chosen = animation && allowed.includes(animation) ? animation : null;
  const guess = guessVerdict(filled.map((c) => c.label));
  const approve =
    filled.find((c) => c.key === approveKey) ?? filled[guess.approve] ?? filled[0];
  const abstainChoice =
    abstainKey === null
      ? undefined
      : (filled.find((c) => c.key === abstainKey) ??
        (abstainKey === undefined && guess.abstain !== null ? filled[guess.abstain] : undefined));
  const abstain = abstainChoice && abstainChoice !== approve ? abstainChoice : undefined;
  const reveal = chosen
    ? revealOf(chosen, {
        ...options,
        approve: approve ? filled.indexOf(approve) : 0,
        abstain: abstain ? filled.indexOf(abstain) : null,
      })
    : null;

  const change = (index: number, patch: Partial<Choice>) =>
    setChoices((list) =>
      list.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    );

  /**
   * ⚠️ L'ORDRE DU TABLEAU EST L'ORDRE EN BASE. Le serveur écrit la position de chaque
   * choix depuis son rang dans ce qu'on lui envoie, donc déplacer ici déplace aussi ce
   * que la salle voit — rien de plus à envoyer.
   */
  const move = (index: number, sens: -1 | 1) =>
    setChoices((list) => moved(list, index, sens));

  const submit = () => {
    const draft: Draft = {
      type,
      title: title.trim(),
      content: content.trim(),
      choices: withChoices
        ? filled.map((c) => ({
            ...(c.id ? { id: c.id } : {}),
            label: c.label.trim(),
            /* Les points et la bonne réponse n'ont de sens que pour un quiz : les
               submit ailleurs remplirait des colonnes que rien ne lit. */
            ...(type === "quiz"
              ? { points: Number(c.points) || 0, isRightAnswer: !!c.isRightAnswer }
              : {}),
          }))
        : [],
      /* Seulement ce que l'organisateur a choisi : le serveur applique ses défauts
         par-dessus le reste. */
      /* En modification on envoie TOUJOURS le réglage : ne pas l'envoyer laisserait
         l'ancienne valeur en place, donc « repasser à une seule réponse » serait un
         geste sans effet. Et on renvoie le `config` lu, pas seulement ce que le
         formulaire règle : le serveur le remplace en entier. */
      ...(withChoices && (maxAnswers > 1 || isEdit || reveal)
        ? {
            config: withReveal({ ...(isEdit ? editing.config : {}), maxAnswers }, reveal),
          }
        : {}),
    };

    save.mutate(draft, {
      onSuccess: () => {
        setTitle("");
        setContent("");
        setChoices([emptyChoice(), emptyChoice()]);
        onCreated?.();
      },
    });
  };

  return (
    <div className="rg__form">
      <div className="rg__seg" role="group" aria-label="Type d'activité">
        {/* ⚠️ EN MODIFICATION, LE TYPE SE LIT, IL NE SE CHOISIT PAS. Les réponses
            déjà enregistrées supposent la forme de l'activité : un quiz devenu nuage
            de mots laisserait des votes rattachés à des choix qui n'ont plus de sens.
            Le serveur refuse aussi de le changer ; le montrer désactivé plutôt que de
            le cacher évite de faire chercher où il est passé. */}
        {(isEdit ? TYPES.filter((t) => t.value === type) : TYPES).map((t) => (
          <button
            key={t.value}
            type="button"
            aria-pressed={type === t.value}
            disabled={isEdit}
            onClick={() => setType(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <p className="hint">
        {isEdit
          ? "Le type ne se change pas : les réponses déjà données en dépendent."
          : TYPES.find((t) => t.value === type)?.help}
      </p>

      <div className="f">
        <label htmlFor="act-titre">Question posée</label>
        <input
          id="act-titre"
          type="text"
          value={title}
          maxLength={255}
          autoComplete="off"
          placeholder={
            watchedOnly
              ? "Ex. le classement du quiz"
              : "Ex. quelle est votre priorité pour 2027 ?"
          }
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="f">
        <label htmlFor="act-consigne">Consigne (facultative)</label>
        <input
          id="act-consigne"
          type="text"
          value={content}
          maxLength={255}
          autoComplete="off"
          placeholder="Ex. une seule réponse"
          onChange={(e) => setContent(e.target.value)}
        />
      </div>

      {withChoices ? (
        <>
          <span className="rgs__lab">Réponses possibles</span>

          {type === "quiz" ? (
            <>
              {/**
               * ⚠️ LE NOMBRE NE DISAIT PAS CE QU'IL ÉTAIT. Une case numérique nue
               * entre le libellé et « Bonne » : l'organisateur a demandé ce que
               * c'était, ce qui est la preuve qu'il fallait l'écrire.
               */}
              <p className="hint">
                Les points sont ce que la réponse rapporte au classement, et ils
                sont indépendants de la bonne réponse : on peut en accorder à une
                réponse partiellement juste, ou marquer la bonne sans point.
              </p>
            </>
          ) : null}

          {/**
           * ⚠️ APRÈS LES RÉPONSES, PAS AVANT. Son plafond est le nombre de réponses
           * remplies : le poser au-dessus obligerait à choisir un maximum avant de
           * savoir sur quoi il porte.
           */}
          {choices.map((choice, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="acte" key={choice.key}>
              {/**
               * ⚠️ DEUX FLÈCHES, PAS UN GLISSÉ, comme la liste des activités : on
               * prépare parfois au clavier, et un glissé-déposé ne s'y fait pas. Elles
               * disent aussi où l'on est — la première ne monte pas, la dernière ne
               * descend pas.
               */}
              <span className="acte__o">
                <button
                  type="button"
                  aria-label={`Monter la réponse ${index + 1}`}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  aria-label={`Descendre la réponse ${index + 1}`}
                  disabled={index === choices.length - 1}
                  onClick={() => move(index, 1)}
                >
                  ↓
                </button>
              </span>
              <input
                type="text"
                value={choice.label}
                maxLength={500}
                autoComplete="off"
                aria-label={`Réponse ${index + 1}`}
                placeholder={`Réponse ${index + 1}`}
                onChange={(e) => change(index, { label: e.target.value })}
              />
              {type === "quiz" ? (
                <>
                  {/**
                   * ⚠️ L'UNITÉ PLUTÔT QU'UN EN-TÊTE DE COLONNE. Il y a eu une
                   * ligne d'intitulés au-dessus : trois mots pour deux champs
                   * courts, dont un rendu plus gros que les autres parce qu'il
                   * héritait de la taille du champ. Le libellé a une place, et
                   * c'est contre la valeur qu'il nomme.
                   */}
                  <label className="acte__p">
                    <input
                      type="number"
                      className="acte__pts"
                      value={choice.points}
                      min={0}
                      aria-label={`Points rapportés par la réponse ${index + 1}`}
                      onChange={(e) =>
                        change(index, { points: Number(e.target.value) || 0 })
                      }
                    />
                    <span>pts</span>
                  </label>
                  <label className="acte__bon">
                    <input
                      type="checkbox"
                      checked={!!choice.isRightAnswer}
                      onChange={(e) =>
                        change(index, { isRightAnswer: e.target.checked })
                      }
                    />
                    Bonne
                  </label>
                </>
              ) : null}
            </div>
          ))}
          <Button
            variant="ghost"
            onClick={() => setChoices((l) => [...l, emptyChoice()])}
          >
            Ajouter une réponse
          </Button>

          {/**
           * ⚠️ APRÈS LES RÉPONSES, PAS AVANT : son plafond est le nombre de réponses
           * remplies, et le poser plus haut demanderait de choisir un maximum avant
           * de savoir sur quoi il porte.
           *
           * ⚠️ IL NE S'AFFICHE QU'À PARTIR DE DEUX RÉPONSES. Sur une seule, « perPerson
           * peut-on en cocher » n'a pas de réponse intéressante, et le champ ne
           * ferait qu'occuper la place.
           */}
          {filled.length > 1 ? (
            <div className="f">
              <label htmlFor="act-perPerson">Réponses par personne</label>
              <input
                id="act-perPerson"
                type="number"
                min={1}
                max={ceiling}
                value={perPerson}
                onChange={(e) => setPerPerson(Number(e.target.value) || 1)}
              />
              <small className="hint">
                {maxAnswers > 1
                  ? `Chacun pourra en cocher jusqu'à ${maxAnswers} sur ${ceiling}.`
                  : "Une seule réponse par personne."}
              </small>
            </div>
          ) : null}

          <RevealFields
            allowed={allowed}
            animation={chosen}
            onAnimation={setAnimation}
            options={options}
            onTune={tune}
            type={type}
            answers={filled}
            approveKey={approve?.key}
            abstainKey={abstain?.key ?? null}
            onApprove={setApproveKey}
            onAbstain={setAbstainKey}
            hasRight={filled.some((c) => c.isRightAnswer)}
          />
        </>
      ) : null}

      {save.isError ? (
        <p className="live__err" role="alert">
          {refusalOf(save.error)}
        </p>
      ) : null}

      <Button loading={save.isPending} disabled={!ready} onClick={submit}>
        {isEdit ? "Enregistrer les modifications" : "Créer l'activité"}
      </Button>
      <p className="hint">
        {isEdit
          ? "Les réponses déjà données restent : les choix sont appariés un à un, pas remplacés."
          : "Elle est créée en brouillon : rien ne s'affiche tant que vous ne l'avez pas lancée."}
      </p>
    </div>
  );
}

/**
 * ═══ LA RÉVÉLATION DES RÉSULTATS ═══
 *
 * ⚠️ À LA PRÉPARATION, PAS AU MOMENT DE MONTRER. Le geste « Montrer les résultats »
 * reste un seul bouton, qu'on presse en direct les yeux sur la salle : lui demander
 * en plus « avec quelle animation ? » ajouterait une décision à l'instant où l'on en
 * veut le moins. Elle se décide la veille, avec la question.
 *
 * ⚠️ ET SES OPTIONS NE S'AFFICHENT QU'AVEC ELLE : « sans animation », elles ne
 * régleraient rien, et un formulaire qui montre des réglages sans effet se lit comme
 * un formulaire cassé.
 */
const ANIMATION_LABELS: Record<RevealAnimation, string> = {
  podium: "Podium",
  answer: "La bonne réponse",
  verdict: "Verdict",
};

const ANIMATION_HELP: Record<RevealAnimation | "none", string> = {
  none: "Les résultats s'affichent d'un coup, en barres, la réponse la plus choisie en tête.",
  podium:
    "Les marches montent, les réponses s'y dévoilent de la dernière place à la première, et la gagnante arrive en dernier.",
  answer:
    "Toutes les réponses s'affichent ; les mauvaises s'éteignent une à une, de la moins choisie à la plus choisie, puis la bonne s'allume. Les parts n'arrivent qu'après.",
  verdict:
    "Une jauge se remplit avec la part des suffrages exprimés qui vaut « pour », jusqu'au trait du seuil, puis le tampon tombe : adoptée ou rejetée.",
};

function RevealFields({
  allowed,
  animation,
  onAnimation,
  options,
  onTune,
  type,
  answers,
  approveKey,
  abstainKey,
  onApprove,
  onAbstain,
  hasRight,
}: {
  allowed: readonly RevealAnimation[];
  animation: RevealAnimation | null;
  onAnimation: (animation: RevealAnimation | null) => void;
  options: RevealOptions;
  onTune: (patch: Partial<RevealOptions>) => void;
  type: ActivityType;
  /** Les réponses remplies, dans l'ordre envoyé. */
  answers: readonly Choice[];
  approveKey: number | undefined;
  abstainKey: number | null;
  onApprove: (key: number) => void;
  onAbstain: (key: number | null) => void;
  /** Quiz : une réponse au moins est cochée « Bonne ». */
  hasRight: boolean;
}) {
  const nameOf = (choice: Choice) => choice.label.trim();

  return (
    <>
      <span className="rgs__lab">Quand on montre les résultats</span>
      <div className="rg__seg" role="group" aria-label="Animation des résultats">
        <button
          type="button"
          aria-pressed={animation === null}
          onClick={() => onAnimation(null)}
        >
          Barres, sans animation
        </button>
        {allowed.map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={animation === value}
            onClick={() => onAnimation(value)}
          >
            {ANIMATION_LABELS[value]}
          </button>
        ))}
      </div>
      <p className="hint">
        {ANIMATION_HELP[animation ?? "none"]}
        {animation ? " Sur l'écran de salle comme sur les téléphones." : ""}
      </p>

      {animation === "podium" ? (
        <>
          <div className="rg__seg" role="group" aria-label="Places sur le podium">
            {(
              [
                [3, "Les trois premières"],
                [1, "La gagnante seule"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={options.places === value}
                onClick={() => onTune({ places: value })}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="rg__seg" role="group" aria-label="Couleurs des marches">
            {(
              [
                [true, "Or, argent, bronze"],
                [false, "Couleurs de l'événement"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={label}
                type="button"
                aria-pressed={options.medals === value}
                onClick={() => onTune({ medals: value })}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      ) : null}

      {/**
       * ⚠️ LE « POUR » ET L'ABSTENTION SONT DEMANDÉS, PAS DEVINÉS EN SILENCE. Le
       * formulaire propose ce que disent les libellés (« Pour », « Abstention »), mais
       * c'est la règle du vote : elle s'écrit sous les yeux de celui qui la réglera.
       */}
      {animation === "verdict" ? (
        <>
          <div className="f">
            <label htmlFor="act-approve">La réponse qui vaut « pour »</label>
            <select
              id="act-approve"
              value={approveKey ?? ""}
              onChange={(e) => onApprove(Number(e.target.value))}
            >
              {answers.map((choice) => (
                <option key={choice.key} value={choice.key}>
                  {nameOf(choice)}
                </option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="act-abstain">L'abstention, hors des suffrages exprimés</label>
            <select
              id="act-abstain"
              value={abstainKey ?? ""}
              onChange={(e) => onAbstain(e.target.value === "" ? null : Number(e.target.value))}
            >
              <option value="">Aucune : toutes les réponses comptent</option>
              {answers
                .filter((choice) => choice.key !== approveKey)
                .map((choice) => (
                  <option key={choice.key} value={choice.key}>
                    {nameOf(choice)}
                  </option>
                ))}
            </select>
          </div>
          <div className="rg__seg" role="group" aria-label="Seuil d'adoption">
            {(
              [
                ["half", "Plus de la moitié"],
                ["two-thirds", "Au moins les deux tiers"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={options.majority === value}
                onClick={() => onTune({ majority: value })}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="f">
            <label htmlFor="act-adopted">Mot du tampon si adoptée (facultatif)</label>
            <input
              id="act-adopted"
              type="text"
              value={options.adopted}
              maxLength={40}
              autoComplete="off"
              placeholder="Adoptée"
              onChange={(e) => onTune({ adopted: e.target.value })}
            />
          </div>
          <div className="f">
            <label htmlFor="act-rejected">Mot du tampon si rejetée (facultatif)</label>
            <input
              id="act-rejected"
              type="text"
              value={options.rejected}
              maxLength={40}
              autoComplete="off"
              placeholder="Rejetée"
              onChange={(e) => onTune({ rejected: e.target.value })}
            />
          </div>
        </>
      ) : null}

      {animation ? (
        <>
          <div className="f">
            <label htmlFor="act-kicker">Phrase d'annonce (facultative)</label>
            <input
              id="act-kicker"
              type="text"
              value={options.kicker}
              maxLength={120}
              autoComplete="off"
              placeholder={
                animation === "answer" ? "La bonne réponse est…" : "Résultat du vote"
              }
              onChange={(e) => onTune({ kicker: e.target.value })}
            />
          </div>

          {animation === "podium" ? (
            <label className="rg__c">
              <input
                type="checkbox"
                checked={options.rest}
                onChange={(e) => onTune({ rest: e.target.checked })}
              />
              Lister les autres réponses sous le podium
            </label>
          ) : null}
          <label className="rg__c">
            <input
              type="checkbox"
              checked={options.confetti}
              onChange={(e) => onTune({ confetti: e.target.checked })}
            />
            {animation === "verdict"
              ? "Confettis si la résolution est adoptée"
              : animation === "answer"
                ? "Confettis sur la bonne réponse"
                : "Confettis sur la gagnante"}
          </label>
        </>
      ) : null}

      {/* Un podium désigne la plus CHOISIE, pas la bonne : sur un quiz, les deux
          divergent dès que la salle se trompe, et c'est souvent tout l'intérêt. */}
      {animation === "podium" && type === "quiz" ? (
        <p className="hint">
          Sur un quiz, le podium classe les réponses les plus choisies ; la bonne
          réponse y garde sa coche, où qu'elle se place.
        </p>
      ) : null}
      {animation === "answer" && !hasRight ? (
        <p className="live__err" role="status">
          Aucune réponse n'est cochée « Bonne » : sans elle, il n'y a rien à révéler, et
          les résultats s'afficheront en barres.
        </p>
      ) : null}
    </>
  );
}
